import MainHeader from '@/components/MainHeader';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Phone, Mail, Cog, Package, Droplets, Users, CheckCircle, Truck, HeadphonesIcon, AlertTriangle, GraduationCap, Warehouse, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ricambiImg from '@/assets/ricambi.png';
import SEOHead from '@/components/SEOHead';
import { ServiceSchema, BreadcrumbSchema } from '@/components/SchemaOrg';

const Ricambi = () => {
  return (
    <>
      <SEOHead
        title="Ricambi Stampanti DTF Certificati | Testine, Damper, Componenti | Materialidtf.it"
        description="Ricambi tecnici certificati per stampanti DTF e DTF UV: testine Epson, damper, componenti elettronici, kit manutenzione. Spedizione express 24/48h in Italia."
        canonical="/ricambi"
        keywords="ricambi stampanti DTF, testine stampa DTF, damper DTF, componenti stampanti DTF, ricambi Epson I3200, manutenzione stampanti DTF"
      />
      <ServiceSchema
        name="Ricambi Tecnici per Stampanti DTF"
        description="Fornitura ricambi certificati per stampanti DTF: testine, damper, componenti elettronici. Tecnici specializzati e spedizione express."
        url="/ricambi"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Ricambi', url: '/ricambi' }
        ]}
      />

      <MainHeader />
      <Navigation />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-primary py-16 px-4">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              DiEffe: Il Tuo Partner per la Stampa DTF
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              Ricambi tecnici certificati e supporto professionale dedicato
            </p>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-12 px-4 bg-muted">
          <div className="container mx-auto flex justify-center">
            <img 
              src={ricambiImg} 
              alt="Ricambi certificati per stampanti DTF - testine, damper, componenti" 
              className="max-w-full md:max-w-2xl rounded-lg shadow-lg"
              loading="lazy"
            />
          </div>
        </section>

        {/* Why Right Parts Matter */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-6">
              ⚙️ Perché i Ricambi Giusti Sono Fondamentali
            </h2>
            <p className="text-center text-muted-foreground max-w-4xl mx-auto text-lg">
              La qualità di stampa DTF dipende direttamente dai componenti utilizzati. Ricambi di bassa qualità possono causare blocchi delle testine, stampe difettose e costose fermate macchina. I nostri tecnici specializzati selezionano solo componenti certificati che garantiscono prestazioni ottimali e durata nel tempo.
            </p>
          </div>
        </section>

        {/* Key Categories */}
        <section className="py-16 px-4 bg-muted">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Cog className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Componenti Essenziali Certificati</h3>
                <p className="text-muted-foreground">
                  Testine di stampa, damper e cavi selezionati dai nostri tecnici per massima efficienza. Solo componenti testati e certificati.
                </p>
              </div>
              <div className="text-center p-8 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Kit Completi Pre-Assemblati</h3>
                <p className="text-muted-foreground">
                  Soluzioni complete per stampanti professionali. Kit pre-assemblati dai nostri tecnici specializzati per installazione rapida.
                </p>
              </div>
              <div className="text-center p-8 bg-card rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Droplets className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Materiali di Consumo</h3>
                <p className="text-muted-foreground">
                  Inchiostri specifici DTF, DTF UV e materiali consumabili certificati per prestazioni ottimali.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Team */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-4">
              👨‍🔧 Il Nostro Team Tecnico Specializzato
            </h2>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Tecnici Certificati al Tuo Servizio</h3>
                  <p className="text-muted-foreground">
                    Il nostro team di tecnici specializzati in stampa DTF ha anni di esperienza nella selezione, installazione e manutenzione di componenti per stampanti professionali. Ogni ricambio viene testato personalmente prima della spedizione.
                  </p>
                </div>
              </div>
              <ul className="space-y-3 ml-20">
                {[
                  'Assistenza tecnica dedicata pre e post vendita',
                  'Consulenza personalizzata sulla scelta dei ricambi',
                  'Video tutorial e guide di installazione dettagliate',
                  'Supporto remoto in caso di necessità'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-foreground">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Guarantees */}
        <section className="py-16 px-4 bg-primary">
          <div className="container mx-auto">
            <h2 className="text-2xl font-bold text-center text-primary-foreground mb-12">
              🎯 La Nostra Garanzia
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-primary-foreground mb-2">Ricambi Testati</h3>
                <p className="text-primary-foreground/80">100% verificati dai nostri tecnici</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-primary-foreground mb-2">Spedizione Express</h3>
                <p className="text-primary-foreground/80">Consegna rapida in 24/48h</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <HeadphonesIcon className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-primary-foreground mb-2">Supporto Continuo</h3>
                <p className="text-primary-foreground/80">Assistenza post-vendita garantita</p>
              </div>
            </div>
          </div>
        </section>

        {/* Catalog */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center text-foreground mb-12">
              🔧 Catalogo Ricambi Tecnici Certificati
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-6 bg-card rounded-lg shadow-md border-l-4 border-primary">
                <h3 className="text-xl font-semibold text-foreground mb-3">Testine e Sistema di Inchiostrazione</h3>
                <p className="text-muted-foreground mb-3">
                  Testine di stampa Epson I3200-A1, DX5, DX7. Damper certificati, filtri professionali e cavi dati testati.
                </p>
                <p className="text-primary font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Selezionati dai nostri tecnici specializzati
                </p>
              </div>
              <div className="p-6 bg-card rounded-lg shadow-md border-l-4 border-primary">
                <h3 className="text-xl font-semibold text-foreground mb-3">Componenti Elettronici Certificati</h3>
                <p className="text-muted-foreground mb-3">
                  Schede madri, driver motori stepper, sensori encoder e componenti elettronici originali e certificati.
                </p>
                <p className="text-primary font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Testati per compatibilità perfetta
                </p>
              </div>
              <div className="p-6 bg-card rounded-lg shadow-md border-l-4 border-primary">
                <h3 className="text-xl font-semibold text-foreground mb-3">Meccanica e Trasmissione</h3>
                <p className="text-muted-foreground mb-3">
                  Cinghie sincrone, pulegge precisione, motori brushless, rulli pressione e componenti meccanici di alta qualità.
                </p>
                <p className="text-primary font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Precisione garantita dai nostri esperti
                </p>
              </div>
              <div className="p-6 bg-card rounded-lg shadow-md border-l-4 border-primary">
                <h3 className="text-xl font-semibold text-foreground mb-3">Sistema di Pulizia Professionale</h3>
                <p className="text-muted-foreground mb-3">
                  Capping station, wiper blade, pompe peristaltiche e kit manutenzione completi assemblati dai nostri tecnici.
                </p>
                <p className="text-primary font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Kit pre-assemblati pronti all'uso
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Warning Section */}
        <section className="py-16 px-4 bg-destructive/10">
          <div className="container mx-auto">
            <h2 className="text-2xl font-bold text-center text-foreground mb-8 flex items-center justify-center gap-2">
              <AlertTriangle className="w-6 h-6 text-destructive" /> Attenzione ai Ricambi Non Certificati
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="flex items-start gap-3 text-foreground">
                <span className="text-destructive font-bold">✕</span>
                <p><strong>Testine contraffatte:</strong> Possono causare danni permanenti alle schede madri e perdite di inchiostro</p>
              </div>
              <div className="flex items-start gap-3 text-foreground">
                <span className="text-destructive font-bold">✕</span>
                <p><strong>Damper di bassa qualità:</strong> Provocano banding, colori sbiaditi e interruzioni nel flusso d'inchiostro</p>
              </div>
              <div className="flex items-start gap-3 text-foreground">
                <span className="text-destructive font-bold">✕</span>
                <p><strong>Componenti meccanici scadenti:</strong> Causano vibrazioni, disallineamenti e riduzione della qualità di stampa</p>
              </div>
              <p className="text-center text-muted-foreground mt-8 text-lg">
                💡 I nostri tecnici certificati verificano ogni componente prima della spedizione per garantirti massima affidabilità
              </p>
            </div>
          </div>
        </section>

        {/* Free Consultation CTA */}
        <section className="py-16 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              🔍 Consulenza Tecnica Gratuita
            </h2>
            <p className="text-muted-foreground text-lg mb-12">
              I nostri tecnici specializzati sono a tua disposizione per identificare il ricambio giusto
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Tecnici Certificati</h3>
                <p className="text-muted-foreground">Esperienza pluriennale in stampa DTF e UV</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Warehouse className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Stock Sempre Disponibile</h3>
                <p className="text-muted-foreground">Magazzino ricambi sempre rifornito</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Spedizione Immediata</h3>
                <p className="text-muted-foreground">Consegna express 24/48 ore in Italia</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href="tel:+393757190556">
                  <Phone className="w-5 h-5 mr-2" />
                  Chiama Ora: +39 375 7190556
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="mailto:info@materialidtf.it">
                  <Mail className="w-5 h-5 mr-2" />
                  Richiedi Preventivo
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Ricambi;
